import { Group } from '@/components/_common';
import StatusTag from '@/components/_common/StatusTag';
import {
  WomBasketBaseResponse,
  WomBasketStatus,
  WomBasketStatusColor,
  WomBasketStatusLabel,
} from '@/core/domain/wom/womBasket/womBasket.types';
import { useWomBasketItemList } from '@/core/domain/wom/womBasketItems/womBasketItems.hooks';
import { Button, Col, Divider, Modal, Row, Table, message } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';
import { useEffect, useMemo, useState } from 'react';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import { WomBasketItem } from '@/core/domain/wom/womBasketItems/womBasketItems.types';
import { useWomBasketDelete } from '@/core/domain/wom/womBasket/womBasket.hooks';
import { useWorkorderComplementSyncWithWom } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.hooks';
import { WorkOrderComplementStatus } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';

interface Props {
  opened: boolean;
  onClose: VoidFunction;
  basket?: WomBasketBaseResponse;
}

interface WomBasketItemVinculation extends WomBasketItem {
  part?: {
    id: number;
    description: string;
  };
}

export default function WomBasketSyncModal(props: Props) {
  const [items, setItems] = useState<WomBasketItemVinculation[]>([]);

  const {
    workorder: { data: workorder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const basketInProgress = props.basket?.status === WomBasketStatus.IN_PROGRESS;

  const womBasketDelete = useWomBasketDelete();

  const womBasketItemList = useWomBasketItemList(
    complement?.id,
    workorder?.id,
    complement?.status === WorkOrderComplementStatus.Open,
    !!props.basket && props.opened,
  );

  const womBasketSync = useWorkorderComplementSyncWithWom();

  useEffect(() => {
    if (props.opened) {
      setItems(womBasketItemList.data || []);
    }
  }, [womBasketItemList.dataUpdatedAt, props.opened]);

  const handleClose = () => {
    setItems([]);
    props.onClose();
  };

  const columns: ColumnsType<WomBasketItemVinculation> = [
    {
      title: 'Tipo',
      render: (_, _item) => <StatusTag label="Peça" color="blue" />,
      width: 100,
    },
    {
      title: 'Descrição',
      dataIndex: 'description',
      ellipsis: true,
    },
    {
      title: 'Cód. Fabricante',
      dataIndex: ['article', 'manufacturerArticleId'],
      width: 100,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: () => <StatusTag label="Em progresso" color="blue" />,
      width: 100,
    },
    {
      title: 'Qtde./Tempo',
      dataIndex: ['article', 'quantity'],
      render: (data) =>
        data ? `${data.unitCount} (${data.quantityPerUoM}x)` : '-',
      align: 'right',
      width: 100,
    },
    {
      title: 'Preço de custo',
      dataIndex: ['article', 'purchasePricePerPriceUnit'],
      align: 'right',
      render: (price) =>
        price
          ? Number(price.value).toLocaleString(price.currency, {
              currency: price.currency,
              style: 'currency',
            })
          : '-',
      width: 100,
    },
    {
      title: 'Preço de venda',
      dataIndex: ['article', 'listPricePerPriceUnit'],
      align: 'right',
      render: (price) =>
        price
          ? Number(price.value).toLocaleString(price.currency, {
              currency: price.currency,
              style: 'currency',
            })
          : '-',
      width: 100,
    },
  ];

  const handleRemove = () => {
    if (!complement?.id || !workorder?.id) {
      return message.warn('Não há uma cesta válida do Bosch WOM');
    }

    Modal.confirm({
      title: 'Excluir cesta Bosch WOM',
      content:
        'Ao remover a cesta não será possível recuperar os dados. Os produtos vinculados a cesta serão removidos do orçamento, você deseja continuar?',
      okText: 'Excluir cesta',
      okButtonProps: { type: 'ghost', danger: true },
      onOk: async () => {
        await womBasketDelete.mutateAsync(complement.id);
        await womBasketSync.mutateAsync({
          complementId: complement.id,
          workOrderId: workorder.id,
        });
        handleClose();
      },
    });
  };

  const modalFooter = useMemo(
    () => (
      <Row gutter={[16, 16]} align="middle" justify="start">
        <Col>
          <Button
            type="ghost"
            danger
            onClick={handleRemove}
            disabled={!basketInProgress}
          >
            Excluir
          </Button>
        </Col>

        <Col flex={1}>
          <Button onClick={handleClose}>Cancelar</Button>
        </Col>
      </Row>
    ),
    [],
  );

  if (!props.basket) return null;

  return (
    <>
      <Modal
        open={props.opened}
        onCancel={handleClose}
        title="Sincronizar cesta Bosch WOM com a OS"
        width="80%"
        footer={modalFooter}
        maskClosable={false}
      >
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }} wrap={false}>
          <Col flex={1}>
            <strong style={{ fontSize: '1.15rem' }}>
              {props.basket?.name}
            </strong>
            <Group>
              <span>N° {props.basket?.buyerOrderNr}</span>{' '}
              <Divider type="vertical" />{' '}
              {props.basket?.status && (
                <StatusTag
                  label={WomBasketStatusLabel[props.basket.status]}
                  color={WomBasketStatusColor[props.basket.status]}
                />
              )}
            </Group>
          </Col>
          <Col>
            <a
              href={props.basket?._links.uiAutoStartSession.href}
              target="_blank"
            >
              <Button block>
                <Group>
                  <FaExternalLinkAlt size={12} />
                  <span>Abrir cesta</span>
                </Group>
              </Button>
            </a>
          </Col>
          <Col>
            <Button
              block
              onClick={() => womBasketItemList.refetch()}
              loading={womBasketItemList.isRefetching}
              type="primary"
            >
              Sincronizar cesta
            </Button>
          </Col>
        </Row>

        <strong style={{ fontSize: '1rem', marginBottom: 24 }}>
          Produtos da cesta
        </strong>

        <Table
          loading={womBasketItemList.isLoading}
          dataSource={items}
          rowKey={(item) => item.itemId}
          columns={columns}
          scroll={{ x: true }}
          pagination={false}
        />
      </Modal>
    </>
  );
}
